/** Notification centre: the bell in the top bar and Account → Notifications. Loaded at sign-in, then kept live from the user's WS channel. */
import { useEffect } from 'react';
import { stripEmoji, type AppNotification } from '@platform/shared';
import { api } from '../lib/api';
import { nudgePush } from '../lib/push';
import { navigate } from '../lib/router';
import { createStore, useStore } from '../lib/store';
import { useMe } from './session';

const PAGE = 30;

/** Older notifications were stored with emoji in their text; the bell shows its own icons instead. */
const clean = (n: AppNotification): AppNotification => ({ ...n, title: stripEmoji(n.title), body: stripEmoji(n.body) });

interface NotificationState {
  items: AppNotification[];
  unread: number;
  /** More, older notifications on the server (Account → Notifications loads them). */
  hasMore: boolean;
  /** Arrived while the client was in the app; shown in the corner (AlertToasts). */
  toasts: AppNotification[];
}

export const notificationStore = createStore<NotificationState>({ items: [], unread: 0, hasMore: false, toasts: [] });

export async function loadNotifications() {
  try {
    const r = await api.get<{ data: AppNotification[]; unread: number }>(`/api/notifications?limit=${PAGE}`);
    notificationStore.set({ items: r.data.map(clean), unread: r.unread, hasMore: r.data.length === PAGE });
  } catch {
    /* the bell just stays as it was */
  }
}

/** The next page of older notifications. */
export async function loadOlderNotifications() {
  const last = notificationStore.get().items.at(-1);
  if (!last) return;
  const r = await api.get<{ data: AppNotification[]; unread: number }>(`/api/notifications?limit=${PAGE}&before=${last.id}`);
  notificationStore.set((s) => {
    const seen = new Set(s.items.map((n) => n.id));
    return { items: [...s.items, ...r.data.filter((n) => !seen.has(n.id)).map(clean)], unread: r.unread, hasMore: r.data.length === PAGE };
  });
}

/** How long a toast that closes itself stays up. */
export const TOAST_MS = 8000;

/** Margin and stop-out stay in the corner until the client acts on them or closes them. */
export const isUrgent = (n: Pick<AppNotification, 'category'>) => !!n.category?.startsWith('risk.');

export const dismissNotificationToast = (id: string) => notificationStore.set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));

/** A notification pushed over the WS channel. */
export function addNotification(raw: AppNotification) {
  const n = clean(raw);
  // A credited deposit is a good moment to offer push alerts.
  if (n.category === 'account.funding' && /credited/i.test(n.title)) nudgePush('deposit');
  if (notificationStore.get().items.some((x) => x.id === n.id)) return;
  // The client's own alerts pop up from the alert event (stores/alerts.ts); don't show them twice.
  const toast = n.category !== 'market.alert' && !n.read;
  notificationStore.set((s) => ({
    items: [n, ...s.items],
    unread: s.unread + (n.read ? 0 : 1),
    toasts: toast ? [n, ...s.toasts].slice(0, 3) : s.toasts,
  }));
  if (toast && !isUrgent(n)) window.setTimeout(() => dismissNotificationToast(n.id), TOAST_MS);
}

/** WS `notification_removed`: a secure link closed, so its bell row and toast go. */
export function removeNotification(id: string) {
  notificationStore.set((s) => {
    const n = s.items.find((x) => x.id === id);
    return {
      items: s.items.filter((x) => x.id !== id),
      unread: n && !n.read ? Math.max(0, s.unread - 1) : s.unread,
      toasts: s.toasts.filter((t) => t.id !== id),
    };
  });
}

/** Mark these notifications read (opening one counts as reading it). */
export async function markRead(ids: string[]) {
  const s = notificationStore.get();
  const fresh = ids.filter((id) => s.items.some((n) => n.id === id && !n.read));
  if (!fresh.length) return;
  notificationStore.set((st) => ({ items: st.items.map((n) => (fresh.includes(n.id) ? { ...n, read: true } : n)), unread: Math.max(0, st.unread - fresh.length) }));
  try {
    const r = await api.post<{ unread: number }>('/api/notifications/read', { ids: fresh });
    notificationStore.set({ unread: r.unread });
  } catch {
    /* next load corrects the count */
  }
}

export async function markAllRead() {
  if (!notificationStore.get().unread) return;
  notificationStore.set((s) => ({ items: s.items.map((n) => ({ ...n, read: true })), unread: 0 }));
  try {
    const r = await api.post<{ unread: number }>('/api/notifications/read', {});
    notificationStore.set({ unread: r.unread });
  } catch {
    /* next load corrects the count */
  }
}

/** Open a notification: it is read, and its link (if any) opens. */
export function openNotification(n: Pick<AppNotification, 'id' | 'link'>) {
  void markRead([n.id]);
  dismissNotificationToast(n.id);
  if (!n.link) return;
  if (n.link.startsWith('/')) navigate(n.link);
  else window.open(n.link, '_blank', 'noopener');
}

/** Mount once: (re)load when the signed-in user changes. */
export function useNotificationsSync() {
  const userId = useMe()?.id ?? null;
  useEffect(() => {
    notificationStore.set({ items: [], unread: 0, hasMore: false, toasts: [] });
    if (userId) void loadNotifications();
  }, [userId]);
}

export const useNotifications = () => useStore(notificationStore, (s) => s.items);
export const useUnreadCount = () => useStore(notificationStore, (s) => s.unread);
export const useHasOlderNotifications = () => useStore(notificationStore, (s) => s.hasMore);
export const useNotificationToasts = () => useStore(notificationStore, (s) => s.toasts);
